// Radha — DSO waterfall (082). CFO track #6.
//
// DSO as one number tells nobody what to do. Broken into slices that each
// have a DIFFERENT OWNER, it becomes a meeting agenda — which is the whole
// point, so the owner is a column, not a footnote.
//
// The bars are a true waterfall: each slice starts where the previous ended,
// so the eye reads the build-up to total DSO. The one thing the schema
// cannot measure — billing delay — is named underneath rather than faked.
import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";

import { supabase } from "../lib/supabase";
import { fmtMoney } from "../lib/money";
import { ExportButton } from "./TableSearch";

interface DsoRow {
  ord: number; component: string; label: string; owner: string;
  amount: string; days: string | null; invoice_count: number | null; note: string;
}

const SLICE_COLOR: Record<string, string> = {
  terms: "var(--fg-muted)",
  late: "var(--status-critical)",
  disputed: "var(--status-serious)",
  deduction: "var(--status-warning)",
  wht: "var(--accent-purple)",
};

function useDsoWaterfall() {
  return useQuery({
    queryKey: ["dso-waterfall"],
    queryFn: async (): Promise<DsoRow[]> => {
      const { data, error } = await supabase.rpc("dso_waterfall");
      if (error) throw error;
      return (data as DsoRow[]) ?? [];
    },
    staleTime: 300_000,
  });
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

export function DsoWaterfall() {
  const { data, isLoading } = useDsoWaterfall();
  if (isLoading || !data || data.length === 0) return null;

  const slices = data.filter((r) => r.ord <= 5);
  const total = data.find((r) => r.ord === 99);
  const daily = data.find((r) => r.ord === 100);
  if (!total) return null;

  // waterfall geometry: an invisible riser, then the slice sitting on it
  let running = 0;
  const chart = slices.filter((s) => Number(s.days) > 0).map((s) => {
    const base = running; running += Number(s.days);
    return { label: s.label, base, value: Number(s.days), comp: s.component };
  });
  chart.push({ label: "Total DSO", base: 0, value: Number(total.days), comp: "total" });

  const worst = slices.reduce((a, b) => (Number(b.days) > Number(a.days) ? b : a), slices[0]);

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>DSO waterfall</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px", maxWidth: 780 }}>
        {Number(total.days)} days of sales outstanding, decomposed by <em>who can actually move it</em>. Each open
        invoice counts in exactly one slice, so the parts sum to the whole. Biggest lever right now:{" "}
        <strong style={{ color: SLICE_COLOR[worst.component] }}>{worst.label} — {Number(worst.days)} days</strong>{" "}
        ({worst.owner}).
      </p>

      <div style={{ width: "100%", height: 250 }}>
        <ResponsiveContainer>
          <BarChart data={chart} margin={{ top: 6, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--fg-muted)" }} axisLine={false} tickLine={false}
              interval={0} height={44} />
            <YAxis tickFormatter={(v: number) => `${v}d`} tick={{ fontSize: 11, fill: "var(--fg-muted)" }}
              axisLine={false} tickLine={false} width={46} />
            <Tooltip
              formatter={(v: number, n: string) => (n === "base" ? null : [`${v} days`, "Contribution"])}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--fg)" }}
              labelStyle={{ color: "var(--fg)" }} itemStyle={{ color: "var(--fg)" }} />
            <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} legendType="none" />
            <Bar dataKey="value" stackId="w" radius={[4, 4, 0, 0]} maxBarSize={64}>
              {chart.map((c) => (
                <Cell key={c.label} fill={c.comp === "total" ? "var(--fg)" : SLICE_COLOR[c.comp] ?? "var(--accent-purple)"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="sanctuary-scroll-x" style={{ marginTop: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
            <th style={th}>Component</th><th style={th}>Owner</th>
            <th style={{ ...th, textAlign: "right" }}>Receivable</th>
            <th style={{ ...th, textAlign: "right" }}>Invoices</th>
            <th style={{ ...th, textAlign: "right" }}>Days</th>
            <th style={th}>What moves it</th>
          </tr></thead>
          <tbody>
            {slices.map((s) => (
              <tr key={s.component} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ ...td, color: SLICE_COLOR[s.component], fontWeight: 600 }}>{s.label}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{s.owner}</td>
                <td style={num}>{fmtMoney(s.amount)}</td>
                <td style={num}>{s.invoice_count}</td>
                <td style={{ ...num, fontWeight: 600 }}>{Number(s.days)}</td>
                <td style={{ ...td, color: "var(--fg-faint)", fontSize: 11.5 }}>{s.note}</td>
              </tr>
            ))}
            <tr style={{ borderTop: "2px solid var(--border-strong)" }}>
              <td style={{ ...td, fontWeight: 700 }}>Total DSO</td>
              <td style={{ ...td, color: "var(--fg-muted)" }}>{total.owner}</td>
              <td style={{ ...num, fontWeight: 600 }}>{fmtMoney(total.amount)}</td>
              <td style={num}>{total.invoice_count}</td>
              <td style={{ ...num, fontWeight: 700 }}>{Number(total.days)}</td>
              <td style={{ ...td, color: "var(--fg-faint)", fontSize: 11.5 }}>
                Daily sales {daily ? fmtMoney(daily.amount) : "—"} over a 365-day window.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 10 }}>
        <p style={{ color: "var(--fg-faint)", fontSize: 11, margin: 0, maxWidth: 640 }}>
          <strong>Not measured:</strong> billing delay — the lag between delivering and raising the invoice. It needs a
          delivery or completion date the schema does not hold, so it is left out rather than estimated; a slice
          invented to round out the story would be the one dishonest bar on this chart.
        </p>
        <span style={{ marginInlineStart: "auto" }}>
          <ExportButton name="DSO waterfall" rows={() => [...slices, total].map((s) => ({
            Component: s.label, Owner: s.owner, Receivable: Number(s.amount),
            Invoices: s.invoice_count, Days: Number(s.days), Note: s.note,
          }))} />
        </span>
      </div>
    </div>
  );
}
